import { DraftCafeStation, DraftCafeStationsSchedule, DraftMenu } from "@dining/api-client";
import { Checkbox, DayOfWeek, Label, PrimaryButton, Stack } from "@fluentui/react";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { enumIterator } from "../../common/commonConstants";
import { addMenuItemToLocalMenuItemBank, setSelectedItem, updateDraftSchedule } from "../../reducers/diningReducer";
import { selectedItemSelector, stationSelector, weekMenuSelector } from "../../selectors/diningSelector";
import styles from "../../styles/dayOfWeekSelector.module.css";

export type DayOfWeekSelectorInput = {
  id: string;
  daysOfWeek: boolean[];
};

export const DayOfWeekSelector: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const selectedItem = useSelector(selectedItemSelector);
  const selectedStation = useSelector(stationSelector);
  const currentMenu = useSelector(weekMenuSelector);
  // Technically we don't need stationMenu but it makes this much easier to understand
  const stationMenu = currentMenu?.stations?.find((station) => station.id === selectedStation?.id);

  // Storage variable.
  let checkboxDefaults = [false, false, false, false, false, false, false];

  // Every time the selectedItem changes, change the 'default'
  // DayOfWeek information of the form to match the selected item.
  useEffect(() => {
    // Search the current station's menu to see if there is a DraftMenu item currently inside of it
    // that matches the id of the currently selected item, then snag it's DayOfWeek information
    let matchedDraftMenuWeekdays = stationMenu?.menu?.find(
      (draftMenu) => draftMenu.menuItemId === selectedItem?.id,
    )?.weekDays;
    // If there is DoW info, change the `checkboxDefaults` values for the DoWs that are
    // present in the matchedDraftMenuWeekdays string to `true`.
    matchedDraftMenuWeekdays
      ? enumIterator(DayOfWeek).forEach((day: string, i: number) => {
          checkboxDefaults[i] = !!matchedDraftMenuWeekdays?.toString().includes(day);
        })
      : null;

    reset({ id: selectedItem?.id, daysOfWeek: checkboxDefaults });
  }, [selectedItem]);

  // React-Hook-Forms initialization and variables
  const {
    control,
    clearErrors,
    handleSubmit,
    reset,
    register,
    formState: { isDirty },
  } = useForm<DayOfWeekSelectorInput>({ mode: "onChange" });

  // When you hit the submit button...
  const onSubmit = (data: { id: string; daysOfWeek: boolean[] }) => {
    const weekDayArray: DayOfWeek[] = [];
    // Convert the form's boolean array into DayOfWeek[].
    data.daysOfWeek.forEach((isSelected: boolean, i: number) => {
      isSelected && weekDayArray.push(DayOfWeek[i] as unknown as DayOfWeek);
    });
    // Add the menu item to the draft menu
    updateMenuItemForDraftMenu(data.id, weekDayArray);
  };

  // If the menu item exists, modify the existing value to reflect the updated DayOfWeek information.
  // Otherwise, create a new DraftMenu object and place it in the selectedStation's menu.
  const updateMenuItemForDraftMenu = (id: string, weekDays: DayOfWeek[]) => {
    // Make a copy of the current menu to modify it without issue
    const deepCopyOfCurrentMenu: DraftCafeStationsSchedule = JSON.parse(JSON.stringify(currentMenu));

    // Try to find the existing station and store it in case there's no current value for the menu item.
    const currentStationData: DraftCafeStation | undefined = deepCopyOfCurrentMenu?.stations?.find(
      (station: DraftCafeStation) => station.id === selectedStation?.id,
    );

    // Try to find the existing menu item.
    const existingMenuItem: DraftMenu | undefined = currentStationData?.menu?.find(
      (draftMenu: DraftMenu) => draftMenu.menuItemId === id,
    );
    // If there is an existing value,
    //    If it has scheduled DoW data, modify it.
    // Otherwise, make a new DraftMenu for the new item.

    // Assuming there is data for the current station (required to get this far in the logic tree)...
    if (currentStationData) {
      // If there's an existing menu item in the station schedule,
      if (currentStationData && existingMenuItem) {
        // and the updated item is scheduled for any days in the week,
        if (weekDays.length > 0) {
          // Change the existing entry to equal the updated Day of Week information.
          existingMenuItem.weekDays = weekDays;
        } else {
          // If it's not scheduled for any days, set weekDays to empty instead of undefined.
          existingMenuItem.weekDays = [];
        }
        // Set isPublished to false to show that the menu item's changes have not been published.
        existingMenuItem.isPublished = false;
        // If there's no entry for the selected item in the draft menu...
      } else {
        // If there's a menu (schedule) for the station,

        // Add the currently selected menu item into the larger menu item bank
        // in case the user switches stations (which wipes the item's details
        // from stationMenuItemBank). This data is not present if the item was
        // not part of the menu during the original call.
        selectedItem && dispatch(addMenuItemToLocalMenuItemBank(selectedItem));
        if (currentStationData?.menu) {
          // Make a new DraftMenu for the item and add it to the existing menu.
          currentStationData?.menu.push(
            DraftMenu.fromJS({
              weekDays: weekDays,
              isPublished: false,
              publishedMenuId: null,
              menuItemId: id,
            }).toJSON(),
          );
        } else {
          // No menu? Make a new one with a new entry in there
          currentStationData.menu = [
            DraftMenu.fromJS({
              weekDays: weekDays,
              isPublished: false,
              publishedMenuId: null,
              menuItemId: id,
            }).toJSON(),
          ];
        }
      }
    } else {
      // If there's no data for the current station in the DraftCafeStationsSchedule, make a new one...
      const newStation = DraftCafeStation.fromJS({
        id: selectedStation?.id,
        name: selectedStation?.name,
        menu: [
          // And make a new DraftMenu object for the newly added item inside it's menu.
          DraftMenu.fromJS({ weekDays: weekDays, isPublished: false, publishedMenuId: null, menuItemId: id }).toJSON(),
        ],
      });
      // Pop the item's info into the menuItemBank...
      selectedItem && dispatch(addMenuItemToLocalMenuItemBank(selectedItem));
      // Then add the new station into the draft schedule.
      deepCopyOfCurrentMenu?.stations
        ? deepCopyOfCurrentMenu.stations.push(newStation)
        : (deepCopyOfCurrentMenu.stations = [newStation]);
    }

    // Replace the current version of the menu with the new one.
    dispatch(updateDraftSchedule(deepCopyOfCurrentMenu));
    // Unselect the item.
    dispatch(setSelectedItem());
  };

  return (
    <Stack className={styles.panel}>
      <Label className={styles.noTopPadding}>Occurs on:</Label>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          className={styles.padTop}
          horizontal={true}
          horizontalAlign={"space-between"}
          verticalAlign={"center"}
          tokens={{ childrenGap: 15 }}
          wrap
        >
          <input
            hidden
            readOnly
            name={"id"}
            value={selectedItem?.id}
            ref={register()}
            data-testid={"hiddenReadOnlyIdField"}
            aria-hidden={true}
          />
          {/* Iterate through the DayOfWeek enum */}
          {enumIterator(DayOfWeek).map((word, i) => {
            const fieldName = `daysOfWeek[${i}]`;
            // Weekends don't get shown, but are required for form validation.
            if (i > 0 && i < 6) {
              // And make a controlled checkbox for each one
              return (
                <Controller
                  control={control}
                  defaultValue={checkboxDefaults[i]}
                  render={({ onChange, onBlur, value }) => (
                    <Checkbox
                      id={fieldName}
                      onChange={(e, checked) => {
                        checked && clearErrors("dayOfWeek");
                        onChange(checked);
                        return checked;
                      }}
                      checked={value}
                      label={word}
                      ariaLabel={`Occurs on: ${word}`}
                      boxSide={"start"}
                      disabled={selectedItem === undefined}
                      // Casting as 'any' is necessary in order to incorporate custom attributes
                      // as per FluentUI docs.
                      inputProps={{ "data-testid": `ItemScheduleCheckboxForDay${word}` } as any}
                    />
                  )}
                  key={word}
                  name={fieldName}
                />
              );
            } else {
              // Hidden checkboxes for Saturday and Sunday
              return (
                <input
                  name={fieldName}
                  disabled
                  readOnly
                  hidden
                  checked={false}
                  key={word}
                  type={"checkbox"}
                  id={fieldName}
                  ref={register()}
                  aria-hidden={true}
                />
              );
            }
          })}
          <Stack.Item align={"end"}>
            <PrimaryButton
              text={
                stationMenu?.menu?.find((draftMenu) => draftMenu.menuItemId === selectedItem?.id)
                  ? "Update"
                  : "Add to menu"
              }
              type={"submit"}
              disabled={selectedItem === undefined || !isDirty}
              data-testid="DoWSubmitBtn"
              className={styles.submitButton}
            />
          </Stack.Item>
        </Stack>
      </form>
    </Stack>
  );
};
